import { useState } from 'react';

interface CartServicesProps {
  cartItems: any[];
  membership: any;
  membershipDiscount?: number;
  getItemPrice: (basePrice: number) => number;
  getDiscountPercent: () => number;
  addService: (name: string, basePrice: number) => void;
  updateQuantity: (index: number, delta: number) => void;
  removeItem: (index: number) => void;
  getTotalItems: () => number;
  membershipPiecesLimit?: number;
  overLimitPieces?: number;
}

const servicesByCategory = {
  obleceni: [
    { name: 'Košile', basePrice: 79 },
    { name: 'Halenka', basePrice: 79 },
    { name: 'Tričko', basePrice: 65 },
    { name: 'Polo tričko', basePrice: 65 },
    { name: 'Mikina', basePrice: 75 },
    { name: 'Svetr', basePrice: 75 },
    { name: 'Kalhoty', basePrice: 75 },
    { name: 'Džíny', basePrice: 69 },
    { name: 'Kraťasy', basePrice: 59 },
    { name: 'Sukně', basePrice: 75 },
    { name: 'Šaty jednoduché', basePrice: 95 },
    { name: 'Šaty složité', basePrice: 125 },
    { name: 'Sako', basePrice: 95 },
    { name: 'Vesta', basePrice: 69 },
    { name: 'Bunda', basePrice: 95 },
    { name: 'Kabát', basePrice: 125 },
  ],
  lozni_pradlo: [
    { name: 'Prostěradlo', basePrice: 69 },
    { name: 'Povlak na peřinu', basePrice: 69 },
    { name: 'Povlak na polštář', basePrice: 35 },
    { name: 'Sada povlečení', basePrice: 150 },
    { name: 'Přehoz na postel', basePrice: 95 },
  ],
  domacnost: [
    { name: 'Ubrus malý', basePrice: 69 },
    { name: 'Ubrus velký', basePrice: 95 },
    { name: 'Ubrousek látkový', basePrice: 20 },
    { name: 'Utěrka', basePrice: 25 },
    { name: 'Ručník', basePrice: 45 },
    { name: 'Osuška', basePrice: 59 },
    { name: 'Záclona (m²)', basePrice: 59 },
    { name: 'Závěs (m²)', basePrice: 69 },
  ],
  specialni: [
    { name: 'Svatební šaty', basePrice: 450 },
    { name: 'Oblek komplet', basePrice: 195 },
    { name: 'Uniforma', basePrice: 95 },
    { name: 'Kravata', basePrice: 45 },
    { name: 'Šátek/šála', basePrice: 45 },
    { name: 'Kapesníček', basePrice: 20 },
  ],
};

const categories = [
  { id: 'obleceni', label: 'Oblečení', icon: 'ri-shirt-line' },
  { id: 'lozni_pradlo', label: 'Ložní prádlo', icon: 'ri-hotel-bed-line' },
  { id: 'domacnost', label: 'Domácnost', icon: 'ri-home-line' },
  { id: 'specialni', label: 'Speciální', icon: 'ri-star-line' },
];

export default function CartServices({
  cartItems,
  membership,
  membershipDiscount = 0,
  getItemPrice,
  getDiscountPercent,
  addService,
  updateQuantity,
  removeItem,
  getTotalItems,
  membershipPiecesLimit = 0,
  overLimitPieces = 0,
}: CartServicesProps) {
  const [activeCategory, setActiveCategory] = useState('obleceni');
  const hasDiscount = (membership || membershipDiscount > 0) && getDiscountPercent() > 0;
  const isOverLimit = hasDiscount && overLimitPieces > 0;

  // Helper to safely retrieve services for the active category
  const servicesForActiveCategory = servicesByCategory[
    activeCategory as keyof typeof servicesByCategory
  ];

  // Calculate per-item discount/full-price split
  const getItemPricingInfo = (itemIndex: number) => {
    if (!hasDiscount || membershipPiecesLimit === 0) {
      return { discountedQty: 0, fullPriceQty: cartItems[itemIndex].quantity, allDiscounted: false, allFullPrice: true };
    }

    let piecesBeforeThisItem = 0;
    for (let i = 0; i < itemIndex; i++) {
      piecesBeforeThisItem += cartItems[i].quantity;
    }

    const currentQty = cartItems[itemIndex].quantity;
    const remainingDiscounted = Math.max(0, membershipPiecesLimit - piecesBeforeThisItem);
    const discountedQty = Math.min(currentQty, remainingDiscounted);
    const fullPriceQty = currentQty - discountedQty;

    return {
      discountedQty,
      fullPriceQty,
      allDiscounted: fullPriceQty === 0,
      allFullPrice: discountedQty === 0,
    };
  };

  return (
    <>
      {/* Add service */}
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
        <h2 className="text-base sm:text-xl font-bold mb-3 sm:mb-4 flex items-center gap-2">
          <i className="ri-add-circle-line text-pink-600 text-lg sm:text-xl"></i>
          Přidat službu
        </h2>

        {/* Category tabs - horizontal scroll on mobile */}
        <div className="flex gap-1.5 sm:gap-2 mb-3 sm:mb-4 overflow-x-auto pb-2 -mx-1 px-1">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium transition-all whitespace-nowrap cursor-pointer flex-shrink-0 ${
                activeCategory === cat.id
                  ? 'bg-pink-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-pink-100'
              }`}
            >
              <i className={`${cat.icon} text-base sm:text-lg`}></i>
              <span className="hidden xs:inline sm:inline">{cat.label}</span>
              <span className="xs:hidden sm:hidden">{cat.label.split(' ')[0]}</span>
            </button>
          ))}
        </div>

        {/* Services grid - 2 columns on mobile */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-1.5 sm:gap-2">
          {servicesForActiveCategory?.map((service, idx) => {
            const discountedPrice = getItemPrice(service.basePrice);
            return (
              <button
                key={idx}
                onClick={() => addService(service.name, service.basePrice)}
                className="px-2 sm:px-3 py-2 bg-gray-100 hover:bg-pink-100 text-gray-800 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium flex flex-col sm:flex-row items-start sm:items-center gap-0.5 sm:gap-2 border-2 border-transparent hover:border-pink-300 cursor-pointer text-left"
              >
                <div className="flex items-center gap-1">
                  <span className="text-pink-600 font-bold">+</span>
                  <span className="line-clamp-1">{service.name}</span>
                </div>
                <span className="text-gray-500 text-xs font-semibold">
                  {hasDiscount ? (
                    <span className="text-teal-600">{discountedPrice} Kč</span>
                  ) : (
                    <span>{service.basePrice} Kč</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Cart items */}
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 mt-4 sm:mt-6">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-base sm:text-xl font-bold flex items-center gap-2">
            <i className="ri-iron-fill text-pink-600 text-lg sm:text-xl"></i>
            Vyžehlené
          </h2>
          <div className="flex items-center gap-2">
            {isOverLimit && (
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-md font-medium">
                {overLimitPieces} ks nad limit
              </span>
            )}
            <span className="text-gray-500 text-sm">{getTotalItems()} ks</span>
          </div>
        </div>

        {cartItems.length === 0 ? (
          <div className="text-center py-8 sm:py-16">
            <i className="ri-shopping-cart-line text-5xl sm:text-7xl text-gray-300 mb-3 sm:mb-4"></i>
            <p className="text-gray-500 text-base sm:text-xl">Váš košík je prázdný</p>
            <p className="text-gray-400 mt-1 sm:mt-2 text-sm">Přidejte služby z nabídky výše</p>
          </div>
        ) : (
          <div className="space-y-2 sm:space-y-4">
            {cartItems.map((item, index) => {
              const pricingInfo = getItemPricingInfo(index);
              const currentPrice = getItemPrice(item.basePrice);

              // Guard against invalid quantities
              const safeQuantity =
                Number.isInteger(item.quantity) && item.quantity > 0 ? item.quantity : 0;

              // Calculate total for this item considering the split
              const discountedTotal = pricingInfo.discountedQty * currentPrice;
              const fullPriceTotal = pricingInfo.fullPriceQty * item.basePrice;
              const itemTotal = discountedTotal + fullPriceTotal;

              return (
                <div
                  key={index}
                  className={`flex flex-col p-2.5 sm:p-4 rounded-xl transition-colors ${
                    pricingInfo.fullPriceQty > 0 && hasDiscount
                      ? 'bg-amber-50 border border-amber-200'
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-2 sm:gap-4">
                    {/* Icon - smaller on mobile */}
                    <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      pricingInfo.allFullPrice && hasDiscount ? 'bg-amber-100' : 'bg-pink-100'
                    }`}>
                      <i className={`ri-shirt-line text-lg sm:text-2xl ${
                        pricingInfo.allFullPrice && hasDiscount ? 'text-amber-600' : 'text-pink-600'
                      }`}></i>
                    </div>

                    {/* Service name and price */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-900 text-sm sm:text-lg truncate">
                        {item.service}
                      </h3>
                      <div className="flex items-center gap-1 sm:gap-2 mt-0.5 flex-wrap">
                        {hasDiscount && pricingInfo.discountedQty > 0 && (
                          <span className="text-xs sm:text-sm font-bold text-teal-600">
                            {currentPrice} Kč
                            {pricingInfo.fullPriceQty > 0 && (
                              <span className="text-xs text-teal-500 font-normal"> × {pricingInfo.discountedQty} ks</span>
                            )}
                          </span>
                        )}
                        {hasDiscount && pricingInfo.fullPriceQty > 0 && pricingInfo.discountedQty > 0 && (
                          <span className="text-gray-300">|</span>
                        )}
                        {pricingInfo.fullPriceQty > 0 && hasDiscount ? (
                          <span className="text-xs sm:text-sm font-bold text-amber-600">
                            {item.basePrice} Kč
                            {pricingInfo.discountedQty > 0 && (
                              <span className="text-xs text-amber-500 font-normal"> × {pricingInfo.fullPriceQty} ks</span>
                            )}
                          </span>
                        ) : !hasDiscount ? (
                          <span className="text-xs sm:text-base font-bold text-gray-700">
                            {item.basePrice} Kč
                          </span>
                        ) : null}
                      </div>
                    </div>

                    {/* Quantity controls - compact on mobile */}
                    <div className="flex items-center gap-0.5 bg-white rounded-lg border border-gray-200">
                      <button
                        onClick={() => updateQuantity(index, -1)}
                        className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-l-lg text-gray-600 hover:bg-gray-200 font-bold cursor-pointer text-sm sm:text-base"
                      >
                        −
                      </button>
                      <span className="w-6 sm:w-8 text-center font-bold text-gray-900 text-sm sm:text-base">
                        {safeQuantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(index, 1)}
                        className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-r-lg text-gray-600 hover:bg-gray-200 font-bold cursor-pointer text-sm sm:text-base"
                      >
                        +
                      </button>
                    </div>

                    {/* Total price */}
                    <div className="text-right min-w-[50px] sm:min-w-[80px]">
                      <div className="font-bold text-teal-600 text-sm sm:text-lg">{itemTotal} Kč</div>
                    </div>

                    {/* Delete button */}
                    <button
                      onClick={() => removeItem(index)}
                      className="w-7 h-7 sm:w-10 sm:h-10 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg cursor-pointer flex-shrink-0"
                    >
                      <i className="ri-delete-bin-line text-base sm:text-xl"></i>
                    </button>
                  </div>

                  {/* Over-limit indicator for this item */}
                  {hasDiscount && pricingInfo.fullPriceQty > 0 && (
                    <div className="mt-1.5 ml-11 sm:ml-16">
                      <span className="text-[10px] sm:text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                        <i className="ri-information-line"></i>
                        {pricingInfo.allFullPrice 
                          ? `${pricingInfo.fullPriceQty} ks za plnou cenu (nad limit členství)`
                          : `${pricingInfo.discountedQty} ks se slevou, ${pricingInfo.fullPriceQty} ks za plnou cenu`
                        }
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
